import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";

/** Plan the week (P8-74): the week's plan from the API, and what the page works out from the choices. */
export type WeekPlan = components["schemas"]["WeekPlan"];
export type PlanRow = WeekPlan["rows"][number];
export type PlanTruck = WeekPlan["trucks"][number];
export type PlanDriver = WeekPlan["drivers"][number];
export type PlanSite = WeekPlan["fillsites"][number];
export type Fit = PlanRow["suggestion"]["fit"];

export const weekPlanQuery = (date: string | undefined) =>
  queryOptions({
    queryKey: ["dispatch-plan", date ?? "this-week"],
    queryFn: () => ok(api.GET("/v1/admin/dispatch/plan", { params: { query: date ? { date } : {} } })),
  });

/** What staff chose for a delivery: the suggestion until they change it, ticked when it can go. */
export interface Choice {
  /** the day it's planned for; null: still in the pool, waiting for staff to pick a day */
  date: string | null;
  fillsite_id: string | null;
  truck_id: string | null;
  driver_id: string | null;
  selected: boolean;
}

export const FIT: Record<Fit, { label: string; tone: "success" | "info" | "warning" | "danger" }> = {
  best: { label: "Best fit", tone: "success" },
  partner: { label: "Partner truck", tone: "info" },
  too_big: { label: "Too big", tone: "danger" },
  trucks_full: { label: "Trucks full", tone: "warning" },
  no_driver: { label: "No driver", tone: "warning" },
  no_fillsite: { label: "No fill site", tone: "warning" },
  waiting_payment: { label: "Waiting for payment", tone: "warning" },
};

/** Why a delivery needs a choice, for the day's plan. */
export const NEEDS: Partial<Record<Fit, string>> = {
  too_big: "Bigger than any truck: it can't be assigned. Change the order to fit a truck, or split it into two orders.",
  trucks_full: "Every truck big enough has its loads for the day.",
  no_driver: "No usual driver is free for a truck big enough: pick a driver.",
  no_fillsite: "No fill site is approved and on.",
  waiting_payment: "The order can't be dispatched until it's paid (or within credit).",
};

export const suggested = (fit: Fit) => fit === "best" || fit === "partner";

/** Planned for another day than its own: assigning it reschedules it. */
export const moves = (row: PlanRow, choice: Choice | undefined) => Boolean(choice?.date && choice.date !== row.date);

/** What can be picked onto a day from the pool: not too big, not waiting for payment, with a fill site. */
export const pickable = (row: PlanRow) => !["too_big", "waiting_payment", "no_fillsite"].includes(row.suggestion.fit);

/** The pool: the flexible deliveries not yet on a day, oldest first. */
export const poolOf = (plan: WeekPlan, choices: Record<string, Choice>) =>
  plan.rows.filter((r) => choices[r.id]?.date === null).sort((a, b) => a.date.localeCompare(b.date));

/**
 * Fills a day from the pool, oldest first, until its truck loads reach the fleet's (a guide: one
 * can still add more by hand). Returns the new choices.
 */
export function fillFromPool(plan: WeekPlan, choices: Record<string, Choice>, day: string) {
  const next = { ...choices };
  for (const r of poolOf(plan, choices)) {
    const counts = dayCounts(plan, next, day);
    if (counts.truck_loads >= counts.capacity_loads) break;
    if (pickable(r)) next[r.id] = { ...(next[r.id] as Choice), date: day };
  }
  return next;
}

/** Each day as planned now: what has a truck already, and what the choices put there. */
export function dayCounts(plan: WeekPlan, choices: Record<string, Choice>, day: string) {
  const server = plan.days.find((d) => d.date === day);
  const rows = plan.rows.filter((r) => choices[r.id]?.date === day);
  const withTruck = (server?.deliveries ?? 0) - (server?.without_truck ?? 0);
  return {
    rows,
    deliveries: withTruck + rows.length,
    without_truck: rows.length,
    overdue: rows.filter((r) => r.overdue).length,
    suggested: rows.filter((r) => suggested(r.suggestion.fit)).length,
    truck_loads: (server?.truck_loads ?? 0) - (server?.without_truck ?? 0) + rows.length,
    capacity_loads: server?.capacity_loads ?? 0,
  };
}

export function initialChoices(plan: WeekPlan): Record<string, Choice> {
  return Object.fromEntries(
    plan.rows.map((r) => [
      r.id,
      {
        // booked ones are on their day; the flexible wait in the pool until staff pick a day
        date: r.flexible ? null : r.suggestion.date,
        fillsite_id: r.suggestion.fillsite_id,
        truck_id: r.suggestion.truck_id,
        driver_id: r.suggestion.driver_id,
        selected: suggested(r.suggestion.fit),
      },
    ]),
  );
}

/** Why the choice can't be assigned, or null: everything chosen, the truck big enough, one fleet, the floor reached. */
export function problemOf(plan: WeekPlan, row: PlanRow, choice: Choice): string | null {
  if (row.suggestion.fit === "waiting_payment") return "Waiting for payment";
  const truck = plan.trucks.find((t) => t.id === choice.truck_id);
  if (!plan.trucks.some((t) => t.capacity_litres >= row.litres)) return "No truck big enough";
  if (!choice.fillsite_id) return "Choose a fill site";
  if (!truck) return "Choose a truck";
  if (truck.capacity_litres < row.litres) return `Holds ${litres(truck.capacity_litres)}`;
  const driver = plan.drivers.find((d) => d.id === choice.driver_id);
  if (!driver) return "Choose a driver";
  if (driver.partner_id !== (truck.partner?.id ?? null)) return "Driver of another fleet";
  if (row.location?.floor != null && row.location.floor > driver.highest_floor)
    return `Driver goes up to floor ${driver.highest_floor}`;
  return null;
}

/** The drivers who can take a truck: of its fleet, its usual driver first. */
export function driversFor(plan: WeekPlan, truck: PlanTruck | undefined) {
  if (!truck) return plan.drivers;
  return plan.drivers
    .filter((d) => d.partner_id === (truck.partner?.id ?? null))
    .sort((a, b) => (b.id === truck.usual_driver_id ? 1 : 0) - (a.id === truck.usual_driver_id ? 1 : 0));
}

/** Each truck's day: what's planned already plus the chosen deliveries ticked. */
export function trucksOfDay(plan: WeekPlan, choices: Record<string, Choice>, day: string) {
  const out = new Map<string, { truck: PlanTruck; loads: number; litres: number; driver: string | null }>();
  for (const p of plan.planned) {
    if (p.date !== day || !p.truck_id) continue;
    const truck = plan.trucks.find((t) => t.id === p.truck_id);
    if (truck) out.set(truck.id, { truck, loads: p.loads, litres: p.litres, driver: null });
  }
  for (const r of plan.rows) {
    const c = choices[r.id];
    if (c?.date !== day || !c.selected || !c.truck_id) continue;
    const truck = plan.trucks.find((t) => t.id === c.truck_id);
    if (!truck) continue;
    const now = out.get(truck.id) ?? { truck, loads: 0, litres: 0, driver: null };
    out.set(truck.id, {
      ...now,
      loads: now.loads + 1,
      litres: now.litres + r.litres,
      driver: plan.drivers.find((d) => d.id === c.driver_id)?.name ?? now.driver,
    });
  }
  return [...out.values()].sort(
    (a, b) => b.loads - a.loads || a.truck.plate_number.localeCompare(b.truck.plate_number),
  );
}

/** Each fill site's expected litres and loads each day: planned, and chosen (ticked). */
export function siteUse(plan: WeekPlan, choices: Record<string, Choice>) {
  const use = new Map<string, { planned: number; chosen: number; loads: number }>();
  const add = (site: string, day: string, field: "planned" | "chosen", l: number, loads: number) => {
    const k = `${site}|${day}`;
    const now = use.get(k) ?? { planned: 0, chosen: 0, loads: 0 };
    use.set(k, { ...now, [field]: now[field] + l, loads: now.loads + loads });
  };
  for (const p of plan.planned) if (p.fillsite_id) add(p.fillsite_id, p.date, "planned", p.litres, p.loads);
  for (const r of plan.rows) {
    const c = choices[r.id];
    if (c?.selected && c.fillsite_id && c.date) add(c.fillsite_id, c.date, "chosen", r.litres, 1);
  }
  return (site: string, day: string) => use.get(`${site}|${day}`) ?? { planned: 0, chosen: 0, loads: 0 };
}

export const litres = (l: number) => `${Math.round(l).toLocaleString("en-KE")} L`;
/** "205k", "1.2M" */
export const shortLitres = (l: number) =>
  l >= 1_000_000 ? `${Math.round(l / 100_000) / 10}M` : l >= 1000 ? `${Math.round(l / 1000)}k` : `${Math.round(l)}`;

const asDate = (day: string) => new Date(`${day}T00:00:00Z`);
/** "Tue 3" */
export const dayShort = (day: string) =>
  new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", timeZone: "UTC" }).format(asDate(day));
/** "1 Oct" */
export const dayMonth = (day: string) =>
  new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }).format(asDate(day));
/** "Tuesday" */
export const weekday = (day: string) =>
  new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: "UTC" }).format(asDate(day));
/** "Tuesday 3 November" */
export const dayLong = (day: string) =>
  new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(
    asDate(day),
  );
/** "2 – 8 November", "28 December – 3 January" */
export function weekRange(from: string, to: string) {
  const f = (day: string, month: boolean) =>
    new Intl.DateTimeFormat("en-GB", { day: "numeric", ...(month ? { month: "long" } : {}), timeZone: "UTC" }).format(
      asDate(day),
    );
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return `${f(from, !sameMonth)} – ${f(to, true)}`;
}
export function addDays(day: string, n: number) {
  const d = asDate(day);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
